import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isNum, sgn, rankText, ymd, fmtN, DeltaRank, DeltaPct, niceMax } from './analytics/format';

// 施策の効果分析。形は依頼者の画面案のとおり（①〜⑤）。集計はサーバー（GbpAnalytics::MeasureEffect）で、
// この画面は条件を URL に入れて effect_data を読み直し、結果を描くだけ。
// 条件の保存は URL で行う（2026-10-20 衣里さん決定）。「保存した分析」はコードに置いた例。
// 画面案の「外部要因メモの期間を除く」（分析メモが保留）と「立地」（データが無い）は無い。

const KEYS = ['ind', 'co', 'pref', 'from', 'to', 'metric', 'method', 'win', 'custom', 'type', 'axis', 'cell', 'dir'];
const DEFAULTS = { metric: 'uu', method: 'net', win: 'type', custom: '21', type: '写真', axis: 'ind', dir: '-1' };
const WIN_OPTIONS = [['type', '種別ごとの評価窓'], ['7', '前後7日'], ['14', '前後14日'], ['28', '前後28日'], ['56', '前後56日'], ['custom', '自由に入力']];
const STATUS = { crit: ['要対応', '#e03131'], warn: ['注意', '#f08c00'], good: ['良好', '#2f9e44'] };

const toQuery = (st) => KEYS.filter((k) => st[k] !== undefined && st[k] !== null && st[k] !== '' && String(st[k]) !== String(DEFAULTS[k] || ''))
  .map((k) => `${k}=${encodeURIComponent(st[k])}`).join('&');

export default function MeasureEffect(props) {
  const { metrics, types, urls, outOfRank = 21 } = props;
  const metricOf = Object.fromEntries(metrics.map((m) => [m.key, m]));
  const typeOf = Object.fromEntries(types.map((t) => [t.key, t]));
  const [st, setSt] = useState(() => {
    const init = { ...DEFAULTS };
    KEYS.forEach((k) => { if (props.initial && props.initial[k] !== undefined) init[k] = String(props.initial[k]); });
    return init;
  });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const seq = useRef(0);

  const query = toQuery(st);
  useEffect(() => {
    try { window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`); } catch (e) { /* 履歴を書けなくても画面は動く */ }
    const my = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      fetch(`${urls.data}?${query}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((json) => { if (my === seq.current) { setRes(json); setError(''); setLoading(false); } })
        .catch(() => { if (my === seq.current) { setError('読み込めませんでした。画面を読み込み直してください'); setLoading(false); } });
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // 対象（業種・会社・地域）を変えたら、選んだマスは外す（そのマスが無くなることがある）
  const set = (patch) => setSt((s) => {
    const next = { ...s, ...patch };
    if (!('cell' in patch) && ['ind', 'co', 'pref', 'axis'].some((k) => k in patch)) delete next.cell;
    return next;
  });
  const applyPreset = (i) => {
    const p = props.presets[i];
    if (!p) return;
    setSt({ ...DEFAULTS, ...Object.fromEntries(Object.entries(p.state).map(([k, v]) => [k, String(v)])) });
    setToast(`「${p.name}」の条件にしました`);
  };
  const copyUrl = () => {
    const url = window.location.href;
    const done = () => setToast('この条件のURLをコピーしました。ブックマークや共有に使えます');
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, () => window.prompt('このURLをコピーしてください', url));
    else window.prompt('このURLをコピーしてください', url);
  };

  const M = metricOf[st.metric] || metrics[0];
  const fE = (v) => {
    if (!isNum(v)) return '—';
    if (M.unit === '位') return `${sgn(v, 2)}位`;
    if (M.unit === 'pt') return `${sgn(v, 2)}pt`;
    return `${sgn(v, 1)}%`;
  };
  const methodLabel = (props.methods.find((m) => m.key === st.method) || {}).label;

  const sel = (label, key, options, extra = {}) => (
    <label className="sd-ctl">{label}
      <select value={st[key] || ''} onChange={(e) => set({ [key]: e.target.value })} {...extra}>
        {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  );

  return (
    <div className="sd ef">
      <div className="sd-card ef-ctl">
        <div className="ef-row"><span className="ef-l">対象</span>
          {sel('業種', 'ind', [['', 'すべて'], ...props.industries.map((o) => [String(o.id), o.label])])}
          {sel('契約会社', 'co', [['', 'すべて'], ...props.companies.map((o) => [String(o.id), o.label])], { className: 'ef-wide' })}
          {sel('地域', 'pref', [['', 'すべて'], ...props.prefs.map((p) => [p, p])])}
          <span className="sd-ctl">施策の実施日
            <input type="date" value={st.from || (res && res.state.from) || ''} min={props.startDate} max={props.endDate}
                   onChange={(e) => e.target.value && set({ from: e.target.value })} aria-label="施策の実施日（から）" />〜
            <input type="date" value={st.to || (res && res.state.to) || ''} min={props.startDate} max={props.endDate}
                   onChange={(e) => e.target.value && set({ to: e.target.value })} aria-label="施策の実施日（まで）" />
          </span>
        </div>
        <div className="ef-row"><span className="ef-l">測り方</span>
          {sel('見る数字', 'metric', metrics.map((m) => [m.key, m.label]))}
          <span className="sd-ctl">効果の測り方
            <select value={st.method} onChange={(e) => set({ method: e.target.value })}>
              {props.methods.map((m) => <option key={m.key} value={m.key}>{m.label}{m.key === 'net' ? '（おすすめ）' : ''}</option>)}
            </select>
            <span className="sd-qm" title={'同業種との差：施策をした店舗の変化から、同じ期間の同業種の店舗の変化を差し引きます。季節やアルゴリズム変動など、全体の動きを取り除けます。\n施策の前と後の差：そのまま比べます。季節の影響が混ざります。\n前年同期との差：同じ店舗の1年前の同じ時期の変化を差し引きます。1年前のデータがある施策だけを数えます。'}>?</span>
          </span>
          <span className="sd-ctl">前後の比較期間
            <select value={st.win} onChange={(e) => set({ win: e.target.value })}>
              {WIN_OPTIONS.map(([v, t]) => <option key={v} value={v}>{v === 'type' ? `${t}（${Array.from(new Set(types.map((x) => x.window))).join('日／')}日）` : t}</option>)}
            </select>
            {st.win === 'custom' && (
              <><input type="number" min="1" max="90" value={st.custom} style={{ width: 70 }} aria-label="前後の日数"
                       onChange={(e) => set({ custom: e.target.value })} /> 日</>
            )}
          </span>
        </div>
        <div className="ef-row"><span className="ef-l">保存</span>
          <label className="sd-ctl">保存した分析
            <select value="" onChange={(e) => applyPreset(Number(e.target.value))}>
              <option value="">選んでください</option>
              {props.presets.map((p, i) => <option key={p.name} value={i}>{p.name}</option>)}
            </select>
          </label>
          <button type="button" className="sd-btn sd-small" onClick={copyUrl}>この条件のURLをコピー</button>
          <a className="sd-btn sd-small" href={`${urls.csv}?${query}`}>⤓ CSV（条件に合う施策）</a>
          <span className="sd-note">
            {res ? `対象の施策 ${fmtN(res.count)}件（評価窓が終わっていない${fmtN(res.unfinished)}件は入れていません）` : ''}
            {loading && <span className="ef-loading"> 集計中…</span>}
          </span>
        </div>
      </div>

      {error && <p className="sl-star-error">{error}</p>}
      {!res ? (
        <div className="sd-card"><div className="sd-body sd-note">集計中です。初めて開いたときは、全店舗の数字を読むので少し時間がかかります…</div></div>
      ) : (
        <div className={`ef-stack${loading ? ' ef-dim' : ''}`}>
          <Step1 res={res} st={st} set={set} M={M} fE={fE} methodLabel={methodLabel} typeOf={typeOf} />
          <Step2 res={res} st={st} methodLabel={methodLabel} typeOf={typeOf} />
          <div className="ef-3grid">
            <Step3 res={res} st={st} set={set} fE={fE} typeOf={typeOf} axes={props.axes} />
            <Conds res={res} st={st} set={set} fE={fE} M={M} methodLabel={methodLabel} />
          </div>
          <Step4 res={res} st={st} urls={urls} outOfRank={outOfRank} />
          <Step5 res={res} st={st} set={set} fE={fE} M={M} urls={urls} outOfRank={outOfRank} />
        </div>
      )}
      {toast && <div className="sd-toast" role="status">{toast}</div>}
    </div>
  );
}

function Chip({ type, typeOf }) {
  const t = typeOf[type] || {};
  return <span className="ef-chip" style={{ '--c': t.color || '#868e96' }}>{type}</span>;
}

function StepHead({ no, title, sub, children }) {
  return (
    <header>
      <span className="ef-no">{no}</span><h2>{title}</h2>{sub && <span className="sd-sub">{sub}</span>}
      {children && <div className="sd-tools">{children}</div>}
    </header>
  );
}

// 枠の幅を測る（viewBox を固定すると、広い枠で文字まで拡大されてしまう）
function useWidth(min = 240) {
  const ref = useRef(null);
  const [w, setW] = useState(400);
  useLayoutEffect(() => {
    const measure = () => { const el = ref.current; if (el && el.clientWidth) setW(Math.max(min, Math.round(el.clientWidth))); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return [ref, w];
}

// ---- ① 施策ごとの効き方 ------------------------------------------------------

function Step1({ res, st, set, M, fE, methodLabel, typeOf }) {
  const rows = res.types.slice().sort((p, q) => (q.n >= 10 ? q.med : -1e9) - (p.n >= 10 ? p.med : -1e9));
  let span = 0;
  rows.forEach((r) => { if (r.n) span = Math.max(span, Math.abs(r.p10), Math.abs(r.p90)); });
  span = (span || 1) * 1.08;
  const X = (v) => ((v + span) / (2 * span)) * 100;
  const ticks = niceTicks(-span, span, 4);
  return (
    <div className="sd-card">
      <StepHead no="1" title="施策ごとの効き方" sub={`${M.label} ・ ${methodLabel} ・ ${res.windowLabel}`} />
      <div className="sd-body">
        <table className="sd-table ef1">
          <thead><tr>
            <th>施策</th><th className="sd-num">件数</th>
            <th>伸びた割合 <span className="sd-qm" title={`${st.method === 'net' ? '同業種より' : ''}${M.label}がプラスだった施策の割合`}>?</span></th>
            <th>変化の分布 <span className="sd-note">箱＝真ん中50% ・ 線＝中央値 ・ ひげ＝10〜90%</span></th>
            <th className="sd-num">中央値</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const few = r.n < 10; const selected = r.type === st.type; const color = (typeOf[r.type] || {}).color;
              const tip = r.n ? `${r.type}（${r.n}件）\n中央値 ${fE(r.med)}\n真ん中50% ${fE(r.q1)} 〜 ${fE(r.q3)}\n伸びた割合 ${Math.round(r.win * 100)}%${few ? '\n10件未満のため参考です' : ''}\nクリックすると②〜⑤がこの施策に切り替わります` : `${r.type}（0件）`;
              return (
                <tr key={r.type} className={`ef-row1${selected ? ' ef-sel' : ''}${few ? ' ef-few' : ''}`} title={tip}
                    onClick={() => set({ type: r.type, cell: '' })}>
                  <td><Chip type={r.type} typeOf={typeOf} />{selected && <span className="ef-selmark">選択中</span>}
                    <span className="sd-sub ef-block">評価窓 前後{r.window}日</span></td>
                  <td className="sd-num">{r.n}{few && r.n > 0 && <span className="sd-sub ef-block">参考</span>}</td>
                  <td><span className="ef-winbar"><i style={{ width: `${((r.win || 0) * 100).toFixed(0)}%` }} /></span>{r.n ? `${Math.round(r.win * 100)}%` : '—'}</td>
                  <td className="ef-boxcell">
                    <svg className="ef-box" viewBox="0 0 100 26" preserveAspectRatio="none" aria-hidden="true">
                      <line x1={X(0)} x2={X(0)} y1="0" y2="26" stroke="#adb5bd" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
                      {r.n > 0 && (
                        <>
                          <line x1={X(r.p10)} x2={X(r.p90)} y1="13" y2="13" stroke="#868e96" vectorEffect="non-scaling-stroke" />
                          <rect x={X(r.q1)} y="5" width={Math.max(X(r.q3) - X(r.q1), 0.3)} height="16" fill={color} fillOpacity=".28" stroke={color} vectorEffect="non-scaling-stroke" />
                          <line x1={X(r.med)} x2={X(r.med)} y1="3" y2="23" stroke="#212529" strokeWidth="2.4" vectorEffect="non-scaling-stroke" />
                        </>
                      )}
                    </svg>
                  </td>
                  <td className="sd-num"><b>{r.n ? fE(r.med) : '—'}</b></td>
                </tr>
              );
            })}
            <tr><td /><td /><td /><td className="ef-boxcell"><div className="ef-axis">
              {ticks.map((v) => <span key={v} style={{ left: `${X(v)}%` }}>{v > 0 ? '+' : v < 0 ? '−' : ''}{Math.abs(v)}{M.unit}</span>)}
            </div></td><td /></tr>
          </tbody>
        </table>
        <p className="sd-note">{M.unit === '位' ? '順位は「上がった＝プラス」にそろえています。' : ''}行をクリックすると、②〜⑤がその施策に切り替わります。</p>
      </div>
    </div>
  );
}

function niceTicks(lo, hi, count) {
  const raw = (hi - lo) / count;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * p).find((s) => s >= raw) || raw;
  const out = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(Math.round(v * 1000) / 1000);
  return out;
}

// ---- ② 効き始めと続く長さ ---------------------------------------------------

const LAG_LABELS = { kw1: 'KW1の順位', uu: 'UU数', act: 'アクション数' };

function Step2({ res, st, methodLabel, typeOf }) {
  const { lag } = res;
  const h = lag.hint;
  return (
    <div className="sd-card">
      <StepHead no="2" title={`${lag.type}のあと、数字はいつ動き、いつまで続くか`} sub="真ん中の線＝中央値 ・ 帯＝真ん中50% ・ 黄色＝今の評価窓 ・ 施策日の前14日の平均と比べた1日ごとの値" />
      <div className="sd-body">
        <div className="ef-evgrid">
          {lag.charts.map((c) => (
            <div key={c.metric} className="ef-ev">
              <div className="ef-evt">{LAG_LABELS[c.metric]} <span className="sd-note">{c.metric === 'kw1' ? '上がった＝プラス' : methodLabel} ・ {c.n}件</span></div>
              <LagChart chart={c} window={lag.window} color={(typeOf[lag.type] || {}).color} />
            </div>
          ))}
        </div>
        <div className="ef-hint"><b>読み方</b>：
          {h.uuPeak ? (
            <>{lag.type}のあと、UU数は<b>{h.uuPeak}日後</b>にいちばん伸び、{h.uuHalf ? <><b>{h.uuHalf}日後</b>には伸びが半分まで戻ります。</> : '56日後まで半分には戻りません。'}</>
          ) : `${lag.type}のあと、UU数にははっきりした伸びが見えません（件数が少ないか、${methodLabel}で見るとプラスになっていません）。`}
          {h.rankPeak ? <>順位は<b>{h.rankPeak}日後</b>にいちばん上がります。</> : ''}
          {h.suggest ? <> → 評価窓の目安は<b>後{h.suggest}日</b>（今の設定：後{lag.window}日）</> : ''}
          <span className="sd-note"> ・ 1日ごとの値は揺れるので、7日の移動平均で読んでいます。{st.method === 'net' ? '' : '同業種との差で見ると、季節の動きが除けます。'}</span>
        </div>
      </div>
    </div>
  );
}

function LagChart({ chart, window: win, color }) {
  const [ref, W] = useWidth(240);
  const H = 180; const ml = 46; const mr = 10; const t = 14; const bot = H - 30;
  const pts = chart.points.filter((p) => p.n >= 10 && isNum(p.med));
  if (!pts.length) return <p className="sd-note" ref={ref}>対象の施策がありません（1日ごとに10件以上必要です）</p>;
  const D0 = -14; const D1 = 56;
  const X = (d) => ml + ((d - D0) / (D1 - D0)) * (W - ml - mr);
  let mx = 0;
  pts.forEach((p) => { mx = Math.max(mx, Math.abs(p.q1), Math.abs(p.q3)); });
  mx = niceMax(mx * 1.05 || 1);
  const Y = (v) => t + (bot - t) * (1 - (v + mx) / (2 * mx));
  const unit = chart.metric === 'kw1' ? '位' : '%';
  const line = pts.map((p, j) => `${j ? 'L' : 'M'}${X(p.o).toFixed(1)} ${Y(p.med).toFixed(1)}`).join('');
  const band = `${pts.map((p, j) => `${j ? 'L' : 'M'}${X(p.o).toFixed(1)} ${Y(p.q3).toFixed(1)}`).join('')}${pts.slice().reverse().map((p) => `L${X(p.o).toFixed(1)} ${Y(p.q1).toFixed(1)}`).join('')}Z`;
  return (
    <div ref={ref}>
      <svg className="sd-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${LAG_LABELS[chart.metric]}の推移`}>
        <rect x={X(1)} y={t} width={Math.max(0, X(Math.min(win, D1)) - X(1))} height={bot - t} fill="#fff3bf" opacity=".75" />
        <text x={X(1) + 4} y={t + 11} className="sd-t-ink2" style={{ fontSize: 10 }}>評価窓（後{win}日）</text>
        {[-mx, 0, mx].map((v) => (
          <g key={v}>
            <line className="sd-grid" x1={ml} x2={W - mr} y1={Y(v)} y2={Y(v)} />
            <text x={ml - 6} y={Y(v) + 3.5} textAnchor="end">{v === 0 ? '0' : `${sgn(v, mx < 5 ? 1 : 0)}${unit}`}</text>
          </g>
        ))}
        <path d={band} fill={color || '#1c7ed6'} opacity=".16" />
        <path d={line} fill="none" stroke={color || '#1c7ed6'} strokeWidth="2" />
        <line x1={X(0)} x2={X(0)} y1={t} y2={bot} stroke="#495057" strokeDasharray="3 3" />
        {[-14, 0, 14, 28, 42, 56].map((d) => <text key={d} x={X(d)} y={H - 12} textAnchor="middle">{d === 0 ? '施策日' : `${d > 0 ? '+' : ''}${d}日`}</text>)}
        {pts.map((p) => (
          <rect key={p.o} x={X(p.o) - 3} y={t} width="6" height={bot - t} fill="transparent">
            <title>{`施策の${p.o < 0 ? `${-p.o}日前` : p.o === 0 ? '当日' : `${p.o}日後`}（${p.n}件）\n中央値 ${sgn(p.med, 1)}${unit}\n真ん中50% ${sgn(p.q1, 1)} 〜 ${sgn(p.q3, 1)}${unit}`}</title>
          </rect>
        ))}
      </svg>
    </div>
  );
}

// ---- ③ どんな条件で効くか ---------------------------------------------------

const divBg = (v, mx) => {
  if (!isNum(v)) return 'transparent';
  const a = (0.08 + 0.5 * Math.min(1, Math.abs(v) / mx)).toFixed(2);
  return v >= 0 ? `rgba(42,120,214,${a})` : `rgba(235,104,52,${a})`;
};

function Step3({ res, st, set, fE, typeOf, axes }) {
  const { heat } = res;
  let mx = 0;
  heat.rows.forEach((r) => r.cells.forEach((c) => { if (c.n >= 10) mx = Math.max(mx, Math.abs(c.med)); }));
  mx = mx || 1;
  const axisLabel = (axes.find((a) => a.key === heat.axis) || {}).label;
  const [cellAx, cellV] = (st.cell || '').split(':');
  return (
    <div className="sd-card">
      <StepHead no="3" title="どんな条件で効くか" sub="マスをクリックすると④⑤がその条件に切り替わります">
        <label className="sd-ctl">分ける軸
          <select value={st.axis} onChange={(e) => set({ axis: e.target.value })}>
            {axes.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </label>
      </StepHead>
      <div className="sd-body">
        <div className="ef-tbl-wrap">
          <table className="ef-heat">
            <thead><tr><th>施策 ／ {axisLabel}</th>{heat.columns.map((c) => <th key={c.v} className="ef-c">{c.label}</th>)}</tr></thead>
            <tbody>
              {heat.rows.map((r) => (
                <tr key={r.type} className={r.type === st.type ? 'ef-selrow' : ''}>
                  <th><Chip type={r.type} typeOf={typeOf} /></th>
                  {r.cells.map((c, j) => {
                    const col = heat.columns[j];
                    if (c.n < 10) return <td key={col.v} className="ef-na" title={`${r.type} × ${col.label}：${c.n}件。10件未満のため数字を出しません`}>n不足<span className="ef-n">{c.n}件</span></td>;
                    const isSel = r.type === st.type && cellAx === heat.axis && cellV === col.v;
                    return (
                      <td key={col.v} className={isSel ? 'ef-selcell' : ''} style={{ background: divBg(c.med, mx) }}
                          title={`${r.type} × ${col.label}\n中央値 ${fE(c.med)}\n真ん中50% ${fE(c.q1)} 〜 ${fE(c.q3)}\n伸びた割合 ${Math.round(c.win * 100)}%\n件数 ${c.n}`}
                          onClick={() => set({ type: r.type, cell: `${heat.axis}:${col.v}`, axis: heat.axis })}>
                        <span className="ef-v">{fE(c.med)}</span><span className="ef-n">{c.n}件・{Math.round(c.win * 100)}%</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="sd-note">青＝伸びた ／ オレンジ＝下がった（色が濃いほど大きい）・ 下の段は件数と伸びた割合 ・ 10件未満のマスは数字を出しません
          {['ind', 'pref'].includes(heat.axis) ? ' ・ 列は施策の多い順に10まで' : ''}</p>
      </div>
    </div>
  );
}

function Conds({ res, st, set, fE, M, methodLabel }) {
  const list = res.conds;
  const top = list.slice(0, 5);
  const bottom = list.slice(-3).reverse().filter((c) => !top.includes(c));
  const mx = Math.max(...list.map((c) => Math.abs(c.med)), 0) || 1;
  const row = (c) => {
    const selected = st.cell === `${c.ax}:${c.v}`;
    const w = (Math.abs(c.med) / mx) * 50;
    return (
      <button type="button" key={`${c.ax}:${c.v}`} className={`ef-cond${selected ? ' ef-sel' : ''}`}
              onClick={() => set({ cell: `${c.ax}:${c.v}`, axis: c.ax })}>
        <span className="ef-cl">{c.axisLabel}：<b>{c.label}</b></span>
        <span className="ef-cbar"><i style={c.med >= 0 ? { left: '50%', width: `${w}%`, background: '#2a78d6' } : { right: '50%', width: `${w}%`, background: '#eb6834' }} /></span>
        <span className="ef-cv">{fE(c.med)}</span>
        <span className="ef-cn">{c.n}件・{Math.round(c.win * 100)}%</span>
      </button>
    );
  };
  return (
    <div className="sd-card">
      <header><h2>{st.type}が効きやすい条件</h2></header>
      <div className="sd-body">
        {!list.length ? <p className="sd-note">15件以上ある条件がありません。対象や実施日の範囲を広げてください。</p> : (
          <>
            <div className="sd-note">{M.label}の中央値（{methodLabel}）。15件以上ある条件だけを出します。クリックすると④⑤がその条件に切り替わります。</div>
            <div className="ef-cgrp">効きやすい</div>{top.map(row)}
            {bottom.length > 0 && <><div className="ef-cgrp">効きにくい</div>{bottom.map(row)}</>}
          </>
        )}
      </div>
    </div>
  );
}

// ---- ④ 横展開できる店舗 -----------------------------------------------------

function Step4({ res, st, urls, outOfRank }) {
  const c = res.candidates;
  const condLabel = c.cond ? `${c.cond.axisLabel}が「${c.cond.label}」${c.cond.auto ? '（いちばん効いた条件）' : ''}` : '条件なし';
  return (
    <div className="sd-card">
      <StepHead no="4" title={`${st.type}を、同じ条件でまだ打っていない店舗`}
                sub={`条件：${condLabel} ・ 直近60日に${st.type}を打っていない ・ ${c.total}店${c.cellNotStore ? '（実施量・季節は店舗の条件ではないため、効いた条件で探しています）' : ''}`} />
      <div className="sd-body sd-flush ef-tbl-wrap">
        <table className="sd-table">
          <thead><tr><th>★</th><th>店舗 ／ Aコード・契約会社</th><th>業種</th><th>エリア</th><th className="sd-num">KW1の順位</th><th className="sd-num">UU数（30日）</th><th>最後に{st.type}</th><th>状態</th><th>担当者</th><th /></tr></thead>
          <tbody>
            {c.rows.map((r) => (
              <tr key={r.id}>
                <td>{r.star ? <span className="ef-star" title={`★ ${r.star.kindLabel}\n${r.star.memo || '（理由の入力なし）'}`}>★</span> : ''}</td>
                <td><b>{r.name}</b><span className="sd-sub ef-block">{[r.code, r.company].filter(Boolean).join(' ・ ')}</span></td>
                <td>{r.industry || '—'}</td>
                <td>{r.pref || '—'}</td>
                <td className="sd-num">{rankText(r.kw1, outOfRank)}<span className="sd-sub ef-block"><DeltaRank value={r.kw1Diff} /></span></td>
                <td className="sd-num">{fmtN(r.uu)}<span className="sd-sub ef-block"><DeltaPct value={r.uuDiff} /></span></td>
                <td>{r.last ? ymd(r.last) : <span className="sd-note">2年以上なし</span>}</td>
                <td>{STATUS[r.status] && <span className="sl-pill" style={{ '--c': STATUS[r.status][1] }}>{STATUS[r.status][0]}</span>}</td>
                <td>{r.staff || '—'}</td>
                <td><a className="sd-btn sd-small" href={`${urls.dashboard}${r.id}`}>ダッシュボード</a></td>
              </tr>
            ))}
            {!c.rows.length && <tr><td colSpan="10" className="sd-note">条件に合う店舗はすべて、直近60日にこの施策を打っています。</td></tr>}
            {c.total > c.rows.length && <tr><td colSpan="10" className="sd-note">ほか {c.total - c.rows.length}店（要対応スコアの高い順に{c.rows.length}店を出しています）</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- ⑤ 事例で確かめる -------------------------------------------------------

const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

function Step5({ res, st, set, fE, M, urls, outOfRank }) {
  const c = res.cases;
  const dir = st.dir || '-1';
  const thirdLabel = st.method === 'yoy' ? '前年同期の変化' : '同業種の変化';
  return (
    <div className="sd-card">
      <StepHead no="5" title="事例で確かめる"
                sub={`${st.type} ・ ${c.cond ? `${c.cond.axisLabel}が「${c.cond.label}」` : 'すべての条件'} ・ ${c.total}件 ・ 行をクリックすると、その店舗のダッシュボードが開きます`}>
        <span className="sd-seg">
          <button type="button" aria-pressed={dir === '-1'} onClick={() => set({ dir: '-1' })}>伸びた順</button>
          <button type="button" aria-pressed={dir === '1'} onClick={() => set({ dir: '1' })}>下がった順</button>
        </span>
      </StepHead>
      <div className="sd-body sd-flush ef-tbl-wrap">
        <table className="sd-table">
          <thead><tr><th>実施日</th><th>店舗 ／ Aコード</th><th>実施内容</th><th className="sd-num">件数</th><th className="sd-num">施策前のKW1</th><th className="sd-num">店舗の変化</th><th className="sd-num">{thirdLabel}</th><th className="sd-num">効果（差）</th></tr></thead>
          <tbody>
            {c.rows.map((r) => (
              <tr key={`${r.sid}-${r.date}-${r.text}`} className="sd-row-click"
                  onClick={() => { window.location.href = `${urls.dashboard}${r.sid}?from=${addDays(r.date, -35)}&to=${addDays(r.date, 42)}&sel=${r.date}`; }}>
                <td>{ymd(r.date)}</td>
                <td><b>{r.name}</b><span className="sd-sub ef-block">{[r.code, r.industry, r.pref].filter(Boolean).join(' ・ ')}</span></td>
                <td className="ef-text">{r.text}</td>
                <td className="sd-num">{isNum(r.qty) ? r.qty : '—'}</td>
                <td className="sd-num">{rankText(r.r0, outOfRank)}</td>
                <td className="sd-num">{fE(r.store)}</td>
                <td className="sd-num">{st.method === 'simple' ? '—' : fE(st.method === 'yoy' ? r.last : r.ind)}</td>
                <td className="sd-num"><b className={r.value >= 0 ? 'sd-d-up' : 'sd-d-down'}>{fE(r.value)}</b></td>
              </tr>
            ))}
            {!c.rows.length && <tr><td colSpan="8" className="sd-note">この条件の施策はありません。</td></tr>}
          </tbody>
        </table>
        <p className="sd-note ef-foot">
          {st.method === 'net' && '効果（差）＝店舗の変化 − 同業種の変化。'}
          {st.method === 'yoy' && '効果（差）＝店舗の変化 − 同じ店舗の前年同期の変化。'}
          単位は{M.unit === '位' ? '位（上がった＝プラス）' : M.unit === 'pt' ? 'ポイント' : '%'}。
        </p>
      </div>
    </div>
  );
}
